import { Facet } from "src/components/containers/CatalogActiveFiltersList/getActiveFilters";
import { Category } from "src/redux/apiTypes";
import { Currency } from "../appConfig/getInitialAppConfig";
import personalizeString from "../cart/personalizeString";
import upperCaseFirstChar from "../js-helpers/upperCaseFirstChar";
import getCategoryLevel from "../marketing/getCategoryLevel";
import { formatPrice } from "../product/formatPrice";
import findCategoryObject from "./findCategoryObject";
import { LocalizeFunc } from "src/services/localization/types";

export interface GetCategorySeoMetaParams {
  categories: Category[];
  selectedCategory?: Category;
  chainName: string;
  firstLevelFilterTitle: string | null;
  localize: LocalizeFunc;
}

export const getTitleWithFilter = (defaultTitle: string, firstLevelFilterTitle: string | null) => {
  return upperCaseFirstChar(firstLevelFilterTitle ? `${defaultTitle} ${firstLevelFilterTitle}` : defaultTitle);
};

export const getFirstLevelFilterTitle = (queryFirstLevel:string, activeFilters: Facet[]):string | null => {
  const name = activeFilters[0]?.option?.name || null;

 return queryFirstLevel && activeFilters.length === 1 ? name : null;
};

export const getCategorySeoTitle = ({
  chainName,
  localize,
  categories,
  selectedCategory,
  firstLevelFilterTitle = null,
}: GetCategorySeoMetaParams) => {
  const dataIsReady = categories?.length > 0 && selectedCategory;

  if (!dataIsReady) {
    return '';
  }

  const categoryName = getTitleWithFilter(selectedCategory.title, firstLevelFilterTitle);
  const parentCategory = findCategoryObject(categories, selectedCategory.parent_id);

  if (parentCategory) {
    const categoryLevel = getCategoryLevel(categories, selectedCategory.id);

    return categoryLevel === 2
      ? `${categoryName} - ${parentCategory.title} ${localize('fast_delivery_from')} ${chainName}`
      : `${personalizeString(localize('third_level_category_title'), [categoryName, parentCategory.title])} ${chainName}`;
  }

  return `${categoryName} - ${localize('delivery_from_store')} ${chainName}`;
};

export interface GetCategoryDescriptionParams extends GetCategorySeoMetaParams {
  minPrice: number;
  maxPrice: number;
  currency: Currency;
  chain: string;
}

export const getCategorySeoDescription = (params: GetCategoryDescriptionParams) => {
  const {
    localize,
    chainName,
    chain,
    categories,
    selectedCategory,
    minPrice,
    maxPrice,
    currency,
    firstLevelFilterTitle = null,
  } = params;
  const dataIsReady = categories?.length > 0 && selectedCategory;

  if (!dataIsReady) {
    return '';
  }

  const title = getTitleWithFilter(selectedCategory.title, firstLevelFilterTitle);
  const categoryLevel = getCategoryLevel(categories, selectedCategory.id);
  const buy = localize('meta.general.buy');
  const withDelivery = `${localize('meta.description.buy_with_delivery')} ${chainName}`;
  const fromPrice = localize(`meta.${chain}.delivery_price_from`);
  const deliveryToCities = `\u{26A1} ${localize('delivery.type.delivery')} ${localize(`meta.${chain}.in_cities`)}`;
  const buyWithDeliveryFromPrice = `${buy} ${withDelivery} ${fromPrice}.`;

  const firstParentCategory = findCategoryObject(categories, selectedCategory.parent_id);

  if (firstParentCategory) {
    if (categoryLevel === 2) {
      const wideRange = localize('meta.description.wide_range');
      return `${title} - ${buyWithDeliveryFromPrice} ${wideRange} - ${firstParentCategory.title} ${deliveryToCities}`;
    }

    if (categoryLevel >= 3) {
      const subCategoryTitle = firstParentCategory.title;
      const foundCategory = findCategoryObject(categories, firstParentCategory.parent_id);
      const firstLevelCategoryTitle = foundCategory?.title || '';
      const currencyText = localize(`seo.currency.${currency}`);
      const pricesFromTo = personalizeString(localize('meta.description.price_from_to'), [
        `${formatPrice(minPrice)} ${currencyText}`,
        `${formatPrice(maxPrice)} ${currencyText}.`,
      ]);
      const chooseFromRange = personalizeString(
        localize('meta.description.choose_wide_range'),
        [subCategoryTitle, firstLevelCategoryTitle],
      );

      return `${upperCaseFirstChar(buy)} ${title} ${withDelivery} ${pricesFromTo} ${chooseFromRange} ${deliveryToCities}`;
    }
  }

  const deliveryMethods = localize('meta.description.delivery_methods');
  const firstLevelDescription = `${title} - ${buyWithDeliveryFromPrice} ${deliveryMethods} ${deliveryToCities}`;
  return firstLevelDescription;
};
